import { useState } from 'react';
import { ChevronLeft, ChevronRight, Dumbbell, Salad } from 'lucide-react';
import { WEEKDAYS, getWorkoutsForDate, getDietsForDate, getWeekDates, getMonthDates } from '../lib/calendar';

export default function CalendarView({ workoutAssignments = [], dietAssignments = [] }) {
  const [view, setView] = useState('week'); // 'week' | 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

  // Navigation
  const prevPeriod = () => {
    const d = new Date(currentDate);
    view === 'week' ? d.setDate(d.getDate() - 7) : d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };
  const nextPeriod = () => {
    const d = new Date(currentDate);
    view === 'week' ? d.setDate(d.getDate() + 7) : d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };

  const today = new Date().toISOString().slice(0, 10);

  const DayCell = ({ date, compact = false }) => {
    if (!date) return <div className="bg-[#0A0A0A] border border-white/5 min-h-[80px]" />;
    const workouts = getWorkoutsForDate(date, workoutAssignments);
    const diets = getDietsForDate(date, dietAssignments);
    const dateStr = date.toISOString().slice(0, 10);
    const isToday = dateStr === today;

    return (
      <div className={`bg-[#121212] border ${isToday ? 'border-white/30' : 'border-white/5'} p-2 min-h-[80px] flex flex-col gap-1`}>
        <span className={`text-xs font-medium ${isToday ? 'text-white' : 'text-[#555]'}`}>
          {compact ? date.getDate() : `${WEEKDAYS[date.getDay()]} ${date.getDate()}`}
        </span>
        {workouts.map((w, i) => (
          <div key={i} className="bg-white/5 border border-white/10 px-1.5 py-0.5 text-xs text-[#E5E5E5] flex items-center gap-1 truncate">
            <Dumbbell size={10} className="text-[#A3A3A3] flex-shrink-0" />
            <span className="truncate">{w.workoutTemplateName || 'Treino'}</span>
            {w.timeOfDay && <span className="text-[#555] flex-shrink-0">{w.timeOfDay[0]}</span>}
          </div>
        ))}
        {diets.length > 0 && (
          <div className="bg-white/5 border border-white/10 px-1.5 py-0.5 text-xs text-[#A3A3A3] flex items-center gap-1 truncate">
            <Salad size={10} className="flex-shrink-0" />
            <span className="truncate">{diets[0].dietTemplateName || 'Dieta'}</span>
          </div>
        )}
      </div>
    );
  };

  const weekDates = getWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate);

  const monthNames = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  // Weekly workout progress (always based on the current calendar week, not navigation)
  const currentWeekDates = (() => {
    const d = new Date();
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return date;
    });
  })();
  const weekWorkoutDays = currentWeekDates.filter(d => getWorkoutsForDate(d).length > 0);
  const weekDoneWorkoutDays = weekWorkoutDays.filter(d => d.toISOString().slice(0, 10) <= today);
  const totalWeekWorkouts = weekWorkoutDays.length;
  const doneWeekWorkouts = weekDoneWorkoutDays.length;

  return (
    <div className="flex flex-col gap-4">
      {/* Weekly progress indicator */}
      {totalWeekWorkouts > 0 && (
        <div className="bg-[#121212] border border-white/5 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#A3A3A3]">
              {doneWeekWorkouts} de {totalWeekWorkouts} treino{totalWeekWorkouts !== 1 ? 's' : ''} realizados esta semana
            </span>
            <span className="text-xs text-[#555]">{Math.round((doneWeekWorkouts / totalWeekWorkouts) * 100)}%</span>
          </div>
          <div className="w-full bg-white/5 h-1.5">
            <div
              className="h-1.5 bg-white transition-all duration-300"
              style={{ width: `${(doneWeekWorkouts / totalWeekWorkouts) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Header: view toggle + navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={prevPeriod} className="text-[#555] hover:text-white transition-colors">
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-medium min-w-[160px] text-center">
            {view === 'week'
              ? `${weekDates[0].getDate()} – ${weekDates[6].getDate()} ${monthNames[weekDates[6].getMonth()]} ${weekDates[6].getFullYear()}`
              : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
            }
          </span>
          <button onClick={nextPeriod} className="text-[#555] hover:text-white transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="flex border border-white/10 overflow-hidden">
          {[['week','Semana'],['month','Mês']].map(([val,lbl]) => (
            <button key={val} onClick={() => setView(val)}
              className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors ${view===val ? 'bg-white text-black' : 'bg-[#121212] text-[#555] hover:text-white'}`}
            >{lbl}</button>
          ))}
        </div>
      </div>

      {/* Week view */}
      {view === 'week' && (
        <div className="grid grid-cols-7 gap-1">
          {weekDates.map((date, i) => (
            <DayCell key={i} date={date} />
          ))}
        </div>
      )}

      {/* Month view */}
      {view === 'month' && (
        <div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map(d => (
              <div key={d} className="text-center text-xs text-[#555] uppercase tracking-widest py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthDates.map((date, i) => (
              <DayCell key={i} date={date} compact />
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-[#555]">
        <span className="flex items-center gap-1"><Dumbbell size={10} /> Treino</span>
        <span className="flex items-center gap-1"><Salad size={10} /> Dieta</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 border border-white/30 inline-block" /> Hoje</span>
      </div>
    </div>
  );
}
